import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { EntityType } from '@erp-attach/shared';
import { Chip } from './ui/Chip';
import { Icon } from './ui/Icon';
import type { IconName } from './ui/Icon';
import { CopyButton } from './CopyButton';

const KIND_LABEL: Record<EntityType, string> = { customer: 'Customer', site: 'Job', supplier: 'Supplier' };
const KIND_ICON: Record<EntityType, IconName> = { customer: 'building', site: 'briefcase', supplier: 'truck' };

function statusVariant(status: string | null | undefined): 'success' | 'warning' | 'info' | 'neutral' {
  const s = (status ?? '').toLowerCase();
  if (!s) return 'neutral';
  if (['active', 'a', 'open'].includes(s)) return 'success';
  if (['inactive', 'i', 'closed', 'archived'].includes(s)) return 'warning';
  return 'info';
}

export interface Crumb {
  label: string;
  href?: string;
}

export interface EntityHeaderProps {
  kind: EntityType;
  name: string;
  number: string;
  branchCode?: string | null;
  status?: string | null;
  subtitle?: ReactNode;
  /** Deterministic deep link (spec §7) for the copy button. */
  deepLink: string;
  actions?: ReactNode;
  /** Crumbs between Home and this record (e.g. the customer a job belongs to). */
  crumbs?: Crumb[];
}

export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav className="gsl-crumbs" aria-label="Breadcrumb">
      <Link to="/">Home</Link>
      {items.map((c, i) => (
        <span key={`${c.label}-${i}`} className="inline-flex items-center gap-1">
          <Icon name="chevron-right" size={13} />
          {c.href ? <Link to={c.href}>{c.label}</Link> : <span aria-current="page">{c.label}</span>}
        </span>
      ))}
    </nav>
  );
}

export function EntityHeader({ kind, name, number, branchCode, status, subtitle, deepLink, actions, crumbs = [] }: EntityHeaderProps) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <Breadcrumbs items={[...crumbs, { label: name }]} />
        <div className="mt-2 flex items-center gap-3">
          <span className="gsl-tile-icon" aria-hidden="true">
            <Icon name={KIND_ICON[kind]} size={20} />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-4xl leading-none">{name}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>
                {KIND_LABEL[kind]} <span className="gsl-num">{number}</span>
              </span>
              {branchCode ? <Chip variant="neutral">{branchCode}</Chip> : null}
              {status ? <Chip variant={statusVariant(status)}>{status}</Chip> : null}
            </div>
          </div>
        </div>
        {subtitle ? <div className="mt-2 text-sm text-muted-foreground">{subtitle}</div> : null}
      </div>
      <div className="flex items-center gap-2">
        {actions}
        <CopyButton text={deepLink} />
      </div>
    </header>
  );
}
